import { EventName } from 'src/features/order/services/order-tealium-services';
import { NotificationMethod, PaymentType } from 'src/services/payment-request-service';
import objTealium from 'src/utils/Tealium/Tealium';

export enum PageName {
  GENERATE_PAYMENT_REQUEST_SENT_SUCCESS_SMS = 'vodapay: smme: self service: generate payment request: sms: payment request success',
  GENERATE_PAYMENT_REQUEST_SENT_SUCCESS_EMAIL = 'vodapay: smme: self service: generate payment request: email: payment request success',
  GENERATE_PAYMENT_REQUEST_SENT_SMS = 'vodapay: smme: self service: generate payment request: sms: payment request success',
  GENERATE_PAYMENT_REQUEST_SENT_EMAIL = 'vodapay: smme: self service: generate payment request: email: payment request success',
  GENERATE_PAYMENT_REQUEST = 'generate payment request',
  GENERATE_PAYMENT_REQUEST_DETAILS_VARIABLE = 'generate payment request: variable: payment request details',
  GENERATE_PAYMENT_REQUEST_DETAILS_FIXED = 'generate payment request: fixed: payment request details',
  GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_EMAIL = 'vodapay: smme: self service: generate payment request: variable: email: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_SMS = 'vodapay: smme: self service: generate payment request: variable: sms: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_EMAIL = 'vodapay: smme: self service: generate payment request: fixed: email: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_SMS = 'vodapay: smme: self service: generate payment request: fixed: sms: payment request summary',
}

export enum UIInteractionType {
  FixedAmount = 'fixed',
  VariableAmount = 'variable',
  Next = 'next',
  SMS = 'sms',
  Email = 'email',
  PhoneNumber = 'phone number',
  EmailAddress = 'email address',
  Help = 'help',
  EmailEntered = 'email address entered',
  PhoneEntered = 'phone number entered',
  Send = 'send',
  Done = 'done',
  Back = 'back'
}

const PAGE_NAME = 'vodapay: smme: self service: ';
const SELF_SERVICE = 'self service';
const PAGE_CHANNEL = 'mobile app';

export enum LinkName {
  GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_EMAIL = 'vodapay: smme: self service: generate payment request: variable: email: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_SMS = 'vodapay: smme: self service: generate payment request: variable: sms: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_EMAIL = 'vodapay: smme: self service: generate payment request: fixed: email: payment request summary',
  GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_SMS = 'vodapay: smme: self service: generate payment request: fixed: sms: payment request summary',
  GENERATE_PAYMENT_REQUEST = 'vodapay: smme: self service: generate payment request',
  GENERATE_PAYMENT_REQUEST_NOTIFICATION_METHOD_VARIABLE = 'vodapay: smme: self service: generate payment request: variable: payment request details',
  GENERATE_PAYMENT_REQUEST_NOTIFICATION_METHOD_FIXED = 'vodapay: smme: self service: generate payment request: fixed: payment request details'
}

type PaymentRequestPageName = 'vodapay: smme: self service: ';
type PaymentRequestLinkName = 'vodapay: smme: self service: generate payment request: ';

export enum JourneyName {
  GENERATE_PAYMENT_REQUEST = 'generate payment request',
}

enum JourneyStepName {
  VARIABLE_PAYMENT = 'variable: payment request details',
  FIXED_PAYMENT = 'fixed: payment request details',
  VARIABLE_SUMMARY = 'variable: payment request summary',
  FIXED_SUMMARY = 'fixed: payment request summary'
}

enum FormType {
  PERSONAL_DETAILS = 'personal details'
}

type View = {
  event_name: EventName | `${EventName}, ${EventName}, ${EventName}` | `${EventName}, ${EventName}` | `${EventName}, payment_request_sent, ${EventName}`;
  page_name?: `${PaymentRequestPageName}${PageName}` | PageName;
  link_page_name?: string;
  link_name?: `${PaymentRequestLinkName}: ${LinkName}`;
  event_label?: string;
  event_value?: string;
  product_category?: 'POS';
  transaction_id?: string;
  event_error_name?: string;
  event_error_code?: 400;
  event_error_type?: 'user error';
  journey_type?: 'self service';
  journey_step_name?: string;
  journey_name?: string;
  page_error_name?: 'technical issue';
  page_error_code?: string;
  page_error_type?: 'system error';
  form_error_type?: 'user error';
  form_error_name?: string;
  form_error_code?: string;
  form_name?: string;
  form_type?: FormType;
  form_step?: string;
  form_step_name?: string;
  payment_preference?: string;
}

interface Link {
  event_name?: EventName | `${EventName}, ${EventName}`;
  page_name?: PageName | `${PaymentRequestPageName}${PageName}` | LinkName;
  link_page_name?: string;
  link_name?: `${LinkName}: ${UIInteractionType}` | `${PageName}: ${UIInteractionType}` | `${LinkName}: ${UIInteractionType} ${UIInteractionType}` | `${LinkName}: ${UIInteractionType}: payment request details: ${UIInteractionType}`;
  link_id?: string;
  product_name?: string;
  journey_name?: JourneyName;
  product_quantity?: number;
  tender_type?: string[];
}

const attempt = (func: () => void) => {
  try {
    func();
  } catch (err) {
    console.error(err);
    // intentional swallowing of non functional error
  }
};

const sendViewToTealium = (obj: View) => {
  attempt(() => {
    objTealium.view(obj);
  });
};

const sendLinkToTealium = (obj: Link) => {
  attempt(() => {
    objTealium.link(obj);
  });
};


const staticTealiumVariables = () => {
  return {
    page_section: SELF_SERVICE,
    page_channel: PAGE_CHANNEL,
    page_type: 'website',
    visitor_login_status: 'logged in',
  };
};

export const PaymentRequestTealiumService = {
  onPaymentRequestStart: () => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_START}`,
      page_name: `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_type: 'self service',
      ...staticTealiumVariables()
    });
  },
  onUIInteraction: (linkName: LinkName, uiInteraction: UIInteractionType | `${UIInteractionType} ${UIInteractionType}` | `${UIInteractionType}: payment request details: ${UIInteractionType}`, preferredPayment?: string, pageName?: PageName | LinkName) => {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: pageName || `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      link_name: `${linkName}: ${uiInteraction}`,
      ...(preferredPayment && { payment_preference: preferredPayment }),
      ...staticTealiumVariables(),
    })
  },
  onPaymentRequestVariableStart: () => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}`,
      page_name: `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST_DETAILS_VARIABLE}`,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_step_name: JourneyStepName.VARIABLE_PAYMENT,
      journey_type: 'self service',
      ...staticTealiumVariables()
    });
  },
  onPaymentRequestFixedStart: () => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}, ${EventName.FORM_START}`,
      page_name: `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST_DETAILS_FIXED}`,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_step_name: JourneyStepName.FIXED_PAYMENT,
      journey_type: 'self service',
      form_name: JourneyStepName.FIXED_PAYMENT,
      form_type: FormType.PERSONAL_DETAILS,
      ...staticTealiumVariables()
    });
  },
  onFilledFormInput: (step: string) => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.FORM_START}`,
      page_name: `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST_DETAILS_FIXED}`,
      form_name: JourneyStepName.FIXED_PAYMENT,
      form_type: FormType.PERSONAL_DETAILS,
      form_step_name: step,
      ...staticTealiumVariables()
    });
  },
  onTechnicalError: (statusCode: string, pageName: PageName) => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.EVENT_ERROR}`,
      page_name: `${PAGE_NAME}${pageName}`,
      page_error_name: 'technical issue',
      page_error_code: statusCode,
      page_error_type: 'system error',
      ...staticTealiumVariables()
    });
  },
  onFormError: (paymentType: PaymentType, errorName: string, errorCode: string) => {
    const pageName = paymentType === PaymentType.FixedAmount ? PageName.GENERATE_PAYMENT_REQUEST_DETAILS_FIXED : PageName.GENERATE_PAYMENT_REQUEST_DETAILS_VARIABLE
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.FORM_ERROR}`,
      page_name: `${PAGE_NAME}${pageName}`,
      form_error_name: errorName,
      form_error_code: errorCode,
      form_error_type: 'user error',
      ...staticTealiumVariables()
    });
  },
  onSummaryVariableStart: (notificationType: NotificationMethod) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_SMS
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}`,
      page_name: pageName,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_step_name: JourneyStepName.VARIABLE_SUMMARY,
      journey_type: 'self service',
      ...staticTealiumVariables()
    });
  },
  onSummaryFixedStart: (notificationType: NotificationMethod) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_SMS
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}`,
      page_name: pageName,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_step_name: JourneyStepName.FIXED_SUMMARY,
      journey_type: 'self service',
      ...staticTealiumVariables()
    });
  },
  onSendRequestFixedPayment: (notificationType: NotificationMethod) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_FIXED_SMS
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: pageName || `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      link_name: `${pageName}: ${UIInteractionType.Send}`,
      ...staticTealiumVariables(),
    })
  },
  onSendRequestVariablePayment: (notificationType: NotificationMethod) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SUMMARY_VARIABLE_SMS
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: pageName || `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      link_name: `${pageName}: ${UIInteractionType.Send}`,
      ...staticTealiumVariables(),
    })
  },
  onRequestSent: (notificationType: NotificationMethod, preferredPayment: string) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SENT_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SENT_SMS
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, payment_request_sent, ${EventName.JOURNEY_END}`,
      page_name: pageName,
      journey_name: JourneyName.GENERATE_PAYMENT_REQUEST,
      journey_type: 'self service',
      payment_preference: preferredPayment,
      ...staticTealiumVariables()
    });
  },
  onRequestSentDone: (notificationType: NotificationMethod) => {
    const pageName = notificationType === NotificationMethod.Email ? PageName.GENERATE_PAYMENT_REQUEST_SENT_EMAIL : PageName.GENERATE_PAYMENT_REQUEST_SENT_SMS
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: pageName || `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      link_name: `${pageName}: ${UIInteractionType.Done}`,
      ...staticTealiumVariables(),
    })
  },
  onBackButton: (pageName: PageName) => {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: pageName || `${PAGE_NAME}${PageName.GENERATE_PAYMENT_REQUEST}`,
      link_name: `${pageName}: ${UIInteractionType.Back}`,
      ...staticTealiumVariables(),
    })
  },
}